import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle, Sparkle } from "lucide-react";
import { WHATSAPP_URL, IMAGES, scrollToSection } from "../constants/site";
import { useLang } from "../i18n/LangContext";

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-[0.08em]">
        <motion.span
            className="block will-change-transform"
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.15, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const Hero = () => {
    const { t } = useLang();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const yImage = useTransform(scrollYProgress, [0, 1], [0, 110]);
    const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);
    const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    return (
        <section
            ref={ref}
            data-testid="hero-section"
            className="hero-glow relative flex min-h-screen items-center overflow-hidden wine-hero pt-28 pb-24"
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -left-40 top-1/3 h-[480px] w-[480px] rounded-full bg-espresso/40 blur-[140px]"
            />

            <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-4 sm:px-8 lg:grid-cols-12 lg:px-12">
                <motion.div style={{ y: yText, opacity }} className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 18 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 2.7 }}
                        className="flex items-center gap-4"
                    >
                        <span className="h-px w-12 bg-gold/60" />
                        <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-gold">
                            {t.hero.eyebrow}
                        </p>
                    </motion.div>

                    <h1
                        data-testid="hero-headline"
                        className="mt-8 font-serif text-5xl leading-[1.02] tracking-tight text-cream sm:text-7xl lg:text-[5.4rem]"
                    >
                        <MaskedLine delay={2.85}>{t.hero.lines[0]}</MaskedLine>
                        <MaskedLine delay={3.0}>
                            <span className="italic text-gold">{t.hero.lines[1]}</span>
                        </MaskedLine>
                        <MaskedLine delay={3.15}>{t.hero.lines[2]}</MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 3.4 }}
                        className="mt-8 max-w-md text-base leading-relaxed text-taupe sm:text-lg"
                    >
                        {t.hero.paragraph}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9, delay: 3.55 }}
                        className="mt-10 flex flex-wrap items-center gap-5"
                    >
                        <a
                            data-testid="hero-cta-whatsapp"
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="group inline-flex items-center gap-3 rounded-full bg-gold px-8 py-4 text-sm font-medium text-noir transition-all duration-300 hover:scale-[1.04] hover:bg-burnt"
                        >
                            <MessageCircle className="h-4 w-4 transition-transform duration-300 group-hover:-rotate-12" />
                            {t.hero.ctaPrimary}
                        </a>
                        <button
                            data-testid="hero-cta-services"
                            onClick={() => scrollToSection("servicos")}
                            className="group inline-flex items-center gap-3 rounded-full border border-beige/30 px-8 py-4 text-sm font-medium text-cream transition-all duration-300 hover:border-gold hover:text-gold"
                        >
                            {t.hero.ctaSecondary}
                            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 3.75 }}
                        className="mt-14 flex flex-wrap items-center gap-x-10 gap-y-4"
                    >
                        {t.hero.stats.map(([value, label]) => (
                            <div key={label}>
                                <p className="font-serif text-3xl text-cream">{value}</p>
                                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-taupe">
                                    {label}
                                </p>
                            </div>
                        ))}
                    </motion.div>
                </motion.div>

                <motion.div
                    style={{ y: yImage }}
                    className="relative lg:col-span-5"
                    initial={{ opacity: 0, y: 60 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2, delay: 3.1, ease: [0.22, 1, 0.36, 1] }}
                >
                    <div className="relative mx-auto max-w-sm lg:max-w-none">
                        <div
                            aria-hidden="true"
                            className="absolute -inset-4 rounded-b-[2rem] rounded-t-[999px] border border-burnt/25"
                        />
                        <div className="overflow-hidden rounded-b-[1.6rem] rounded-t-[999px] border border-gold/30">
                            <motion.img
                                data-testid="hero-image"
                                src={IMAGES.hero}
                                alt="Unhas com esmaltação premium em tons dourados"
                                initial={{ scale: 1.18 }}
                                animate={{ scale: 1 }}
                                transition={{ duration: 1.8, delay: 3.1, ease: [0.22, 1, 0.36, 1] }}
                                className="h-[440px] w-full object-cover sm:h-[560px]"
                            />
                        </div>

                        <div className="absolute -right-6 -top-6 h-28 w-28 sm:-right-10">
                            <svg viewBox="0 0 100 100" className="h-full w-full animate-spin-slow">
                                <defs>
                                    <path
                                        id="circlePath"
                                        d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0"
                                    />
                                </defs>
                                <text className="fill-gold" style={{ fontSize: "8.2px", letterSpacing: "2.6px" }}>
                                    <textPath href="#circlePath">
                                        YASMIM NAILS · UBATUBA · STUDIO ·
                                    </textPath>
                                </text>
                            </svg>
                            <Sparkle className="absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 text-burnt" />
                        </div>
                    </div>
                </motion.div>
            </div>

            <motion.button
                data-testid="hero-scroll-indicator"
                onClick={() => scrollToSection("servicos")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 4.3, duration: 1 }}
                className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-taupe transition-colors hover:text-gold md:flex"
                aria-label="Rolar para baixo"
            >
                <span className="font-mono text-[10px] uppercase tracking-[0.35em]">
                    {t.hero.scroll}
                </span>
                <motion.span
                    animate={{ y: [0, 8, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                >
                    <ArrowDown className="h-4 w-4" />
                </motion.span>
            </motion.button>
        </section>
    );
};

export default Hero;
